import type { XkEvent } from '@/lib/api/types';
import { eventPhase, type EventPhase } from '@/features/project/eventPhase';
import type { EventFacts, EventFactsFunnel, EventFactsLedgerRow } from './factsApi';

/**
 * El motor de los dashboards. Determinista, sin red, sin React: recibe los
 * HECHOS que cuenta el servidor (`factsApi.ts`) y devuelve lo que se pinta.
 *
 * Reglas que valen en todas las pantallas:
 *  - Toda tasa sale dentro de un sobre (`Metric`) que dice si está MEDIDA o
 *    PENDIENTE y qué le falta. Un dato ausente es ausente, nunca cero: un
 *    artículo sin coste deja el valor distribuido pendiente en vez de
 *    abaratarlo.
 *  - El mismo hecho es el mismo dígito en el informe del evento, en el del
 *    proyecto y en Insights, porque los tres pasan por aquí.
 */

export type MetricUnit = 'percent' | 'count' | 'usd';

export type MetricNeed =
  | 'unitCosts'
  | 'allocations'
  | 'consumedUnits'
  | 'prizesWon'
  | 'contacts'
  | 'interactions'
  | 'leads'
  | 'peakBlock'
  | 'captures'
  | 'leadGoal'
  | 'distributedValue'
  | 'footprintTraffic';

export interface Metric {
  state: 'MEASURED' | 'PENDING';
  value: number | null;
  unit: MetricUnit;
  numerator?: number | null;
  base?: number | null;
  /** Margen de error al 95 %, en puntos, sólo para porcentajes. */
  moe?: number | null;
  /**
   * Qué falta para medirlo, cuando está pendiente: una clave de `dash.need.*`
   * (la pantalla la traduce), nunca una frase.
   */
  needs?: MetricNeed;
  /** Cuántos faltan, para las claves que cuentan (`unitCosts`). */
  needsCount?: number;
  /** Base < 50: la cifra orienta, no concluye. */
  directional?: boolean;
}

const r1 = (n: number) => Math.round(n * 10) / 10;
const r2 = (n: number) => Math.round(n * 100) / 100;

/** Porcentaje `numerator / base`, con su margen de error. */
export function env(numerator: number | null | undefined, base: number | null | undefined, needs?: MetricNeed): Metric {
  if (numerator == null || base == null || base <= 0) {
    return { state: 'PENDING', value: null, unit: 'percent', numerator: numerator ?? null, base: base ?? null, needs };
  }
  const p = numerator / base;
  return {
    state: 'MEASURED',
    value: r1(p * 100),
    unit: 'percent',
    numerator,
    base,
    moe: r1(1.96 * Math.sqrt((p * (1 - p)) / base) * 100),
    directional: base < 50,
  };
}

export function count(value: number | null | undefined, needs?: MetricNeed): Metric {
  if (value == null) return { state: 'PENDING', value: null, unit: 'count', needs };
  return { state: 'MEASURED', value, unit: 'count' };
}

export function money(value: number | null | undefined, needs?: MetricNeed, needsCount?: number): Metric {
  if (value == null) return { state: 'PENDING', value: null, unit: 'usd', needs, needsCount };
  return { state: 'MEASURED', value: r2(value), unit: 'usd' };
}

/** Margen de error (95 %) de una proporción, en puntos; null sin base. */
export function moe(numerator: number, base: number): number | null {
  if (!base) return null;
  const p = numerator / base;
  return r1(1.96 * Math.sqrt((p * (1 - p)) / base) * 100);
}

// ── Formato ────────────────────────────────────────────────────────────────

export function fmtInt(n: number | null | undefined, pending = '—'): string {
  return n == null ? pending : Math.round(n).toLocaleString();
}

export function fmtUsd(v: number | null | undefined, pending = '—'): string {
  return v == null
    ? pending
    : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtPct(v: number | null | undefined, pending = '—', digits = 1): string {
  return v == null ? pending : `${v.toFixed(digits)}%`;
}

/** Lo grande de una métrica, listo para pintar. */
export function bigOf(m: Metric, pending = '—'): string {
  if (m.state === 'PENDING' || m.value == null) return pending;
  if (m.unit === 'percent') return fmtPct(m.value);
  if (m.unit === 'usd') return fmtUsd(m.value);
  return fmtInt(m.value);
}

/** Dos letras para la ficha de un artículo: «Water Bottle» → «WB». */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

// ── Fase del evento ────────────────────────────────────────────────────────

export function phaseOf(f: EventFacts, now = Date.now()): EventPhase {
  return eventPhase(
    {
      startDate: f.event.startDate,
      endDate: f.event.endDate,
      eventStatus: f.event.eventStatus,
    } as unknown as XkEvent,
    now,
  );
}

/** Fracción de la ventana del evento ya transcurrida; null sin fechas. */
export function windowFraction(start: string | null, end: string | null, now = Date.now()): number | null {
  if (!start || !end) return null;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;
  return Math.max(0, Math.min(1, (now - s) / (e - s)));
}

// ── Libro de premios ───────────────────────────────────────────────────────

export interface LedgerTotals {
  rows: EventFactsLedgerRow[];
  allocated: number;
  inPlay: number;
  /** allocated − inPlay − returned: lo que salió del juego hacia alguien. */
  consumed: number;
  /** Devuelto al almacén al cerrar. */
  returned: number;
  won: number;
  delivered: number;
  pending: number;
  expired: number;
  voided: number;
  /** Salió del juego sin registro de canje: sin destino conocido. */
  unaccounted: number;
  /** Σ esperado de la calculadora (CH-09); null si ningún artículo lo tiene. */
  expected: number | null;
  unaccountedRate: Metric;
  redemption: Metric;
  /** Σ delivered × unitCost; pendiente si a algún artículo le falta coste. */
  distributedValue: Metric;
  costedRows: number;
  missingCost: EventFactsLedgerRow[];
}

export function unaccountedOf(row: Pick<EventFactsLedgerRow, 'allocated' | 'inPlay' | 'returned' | 'won' | 'voided' | 'dispositioned'>): number {
  // Lo que salió del juego sin canje y aún no tiene explicación: una disposición
  // (entregado fuera de estación, uso de operaciones, merma, recuento) lo resta.
  return Math.max(0, row.allocated - row.inPlay - row.returned - row.won - row.voided - (row.dispositioned ?? 0));
}

export function ledgerOf(rows: EventFactsLedgerRow[]): LedgerTotals {
  const sum = (f: (r: EventFactsLedgerRow) => number) => rows.reduce((a, r) => a + f(r), 0);
  const allocated = sum((r) => r.allocated);
  const inPlay = sum((r) => r.inPlay);
  const won = sum((r) => r.won);
  const delivered = sum((r) => r.delivered);
  const pending = sum((r) => r.pending);
  const expired = sum((r) => r.expired);
  const voided = sum((r) => r.voided);
  const returned = sum((r) => r.returned);
  const withExpected = rows.filter((r) => r.expected != null);
  const expected = withExpected.length ? withExpected.reduce((a, r) => a + (r.expected ?? 0), 0) : null;
  const consumed = Math.max(0, allocated - inPlay - returned);
  const unaccounted = sum(unaccountedOf);
  const missingCost = rows.filter((r) => r.unitCost == null);
  const costed = rows.filter((r) => r.unitCost != null);
  return {
    rows,
    allocated,
    inPlay,
    consumed,
    returned,
    won,
    delivered,
    pending,
    expired,
    voided,
    unaccounted,
    expected,
    unaccountedRate: env(unaccounted, consumed, 'consumedUnits'),
    redemption: env(delivered, won, 'prizesWon'),
    distributedValue:
      rows.length === 0
        ? money(null, 'allocations')
        : missingCost.length
          ? money(null, 'unitCosts', missingCost.length)
          : money(costed.reduce((a, r) => a + (r.distributedValue ?? r.delivered * (r.unitCost ?? 0)), 0)),
    costedRows: costed.length,
    missingCost,
  };
}

/** Une los libros de varios eventos por artículo (para proyecto e Insights). */
export function mergeLedgers(list: readonly EventFacts[]): EventFactsLedgerRow[] {
  const by = new Map<number, EventFactsLedgerRow>();
  for (const f of list) {
    for (const r of f.ledger) {
      const cur = by.get(r.stockId);
      if (!cur) {
        by.set(r.stockId, { ...r });
        continue;
      }
      cur.allocated += r.allocated;
      cur.inPlay += r.inPlay;
      cur.returned += r.returned;
      cur.expected = r.expected == null && cur.expected == null ? null : (cur.expected ?? 0) + (r.expected ?? 0);
      cur.dispositioned = (cur.dispositioned ?? 0) + (r.dispositioned ?? 0);
      cur.won += r.won;
      cur.delivered += r.delivered;
      cur.pending += r.pending;
      cur.expired += r.expired;
      cur.voided += r.voided;
      cur.distributedValue =
        cur.unitCost == null || r.distributedValue == null || cur.distributedValue == null
          ? null
          : r2(cur.distributedValue + r.distributedValue);
      if (cur.unitCost == null && r.unitCost != null) cur.unitCost = r.unitCost;
      if (!cur.image && r.image) cur.image = r.image;
    }
  }
  return [...by.values()].sort((a, b) => b.delivered - a.delivered || b.allocated - a.allocated);
}

// ── Embudo de captura ──────────────────────────────────────────────────────

export type FunnelKey = 'interactions' | 'contacts' | 'leads' | 'responses' | 'prizesWon' | 'prizesDelivered';
export const FUNNEL_ORDER: FunnelKey[] = ['interactions', 'contacts', 'leads', 'responses', 'prizesWon', 'prizesDelivered'];

export interface FunnelStep {
  key: FunnelKey;
  value: number | null;
  /** Como parte del primer escalón. */
  ofTop: Metric;
  /** Caída respecto al escalón anterior, en puntos; null si no aplica. */
  drop: number | null;
  /** No se pudo medir (p. ej., ningún despliegue con encuesta). */
  absent: boolean;
}

export function funnelOf(f: EventFactsFunnel, opts: { surveyed?: boolean } = {}): FunnelStep[] {
  const values: Record<FunnelKey, number | null> = {
    interactions: f.interactions,
    contacts: f.contacts,
    leads: f.leads,
    // Sin despliegue con encuesta no hubo nada que completar: ausente, no cero.
    responses: opts.surveyed === false ? null : f.responses,
    prizesWon: f.prizesWon,
    prizesDelivered: f.prizesDelivered,
  };
  const top = f.interactions;
  let prev: number | null = null;
  return FUNNEL_ORDER.map((key) => {
    const v = values[key];
    const step: FunnelStep = {
      key,
      value: v,
      ofTop: v == null || !top ? env(null, null, 'interactions') : env(v, top),
      drop: v == null || prev == null || prev === 0 ? null : r1((1 - v / prev) * 100),
      absent: v == null,
    };
    if (v != null) prev = v;
    return step;
  });
}

export function sumFunnels(list: readonly EventFacts[]): EventFactsFunnel {
  const out: EventFactsFunnel = {
    interactions: 0,
    responses: 0,
    contacts: 0,
    leads: 0,
    marketingOptIns: 0,
    prizesWon: 0,
    prizesDelivered: 0,
  };
  for (const f of list) {
    out.interactions += f.funnel.interactions;
    out.responses += f.funnel.responses;
    out.contacts += f.funnel.contacts;
    out.leads += f.funnel.leads;
    out.marketingOptIns += f.funnel.marketingOptIns;
    out.prizesWon += f.funnel.prizesWon;
    out.prizesDelivered += f.funnel.prizesDelivered;
  }
  return out;
}

// ── Throughput: capturas por bloque de 15 minutos ──────────────────────────

export const BLOCK_MINUTES = 15;
export type ThroughputPhase = 'warmup' | 'peak' | 'tail';

export interface ThroughputBlock {
  /** Inicio del bloque, ISO. */
  at: string;
  ms: number;
  count: number;
  phase: ThroughputPhase;
  /** El bloque en curso ahora mismo. */
  live: boolean;
}

export interface ThroughputSeries {
  /** Día local `YYYY-MM-DD`. */
  day: string;
  /** El evento, cuando la lista mezcla varios (Insights). */
  name?: string;
  /** Rótulo del día cuando el día es sintético (agregado por hora del día). */
  dayLabel?: string;
  blocks: ThroughputBlock[];
  total: number;
  peak: number;
  peakIndex: number;
  /** Cuántos bloques concentran la mitad de las capturas. */
  peakWindowBlocks: number;
  /** Media por bloque como parte del pico. */
  utilization: Metric;
  warmup: number;
  peakSum: number;
  tail: number;
  /** Número de bloques de cada tercio (redondeado arriba). */
  third: number;
}

function localDay(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Agrupa los bloques por día local y rellena los huecos de 15 minutos entre
 * el primero y el último de cada día, para que la línea de tiempo tenga la
 * forma del día y no la de los datos. Un día sin capturas no existe.
 */
export function throughputOf(raw: ReadonlyArray<{ at: string; count: number }>, now = Date.now()): ThroughputSeries[] {
  const blockMs = BLOCK_MINUTES * 60_000;
  const byDay = new Map<string, Map<number, number>>();
  for (const b of raw) {
    const ms = new Date(b.at).getTime();
    if (!Number.isFinite(ms)) continue;
    const day = localDay(ms);
    const m = byDay.get(day) ?? new Map<number, number>();
    m.set(ms, (m.get(ms) ?? 0) + b.count);
    byDay.set(day, m);
  }
  const liveBlock = Math.floor(now / blockMs) * blockMs;
  return [...byDay.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([day, m]) => {
      const keys = [...m.keys()].sort((a, b) => a - b);
      const first = keys[0]!;
      const last = keys[keys.length - 1]!;
      // Nunca más de un día de bloques, por si un reloj mal puesto mete un
      // punto a las 3 de la mañana.
      const n = Math.min(96, Math.floor((last - first) / blockMs) + 1);
      const values: number[] = [];
      for (let i = 0; i < n; i++) values.push(m.get(first + i * blockMs) ?? 0);
      const total = values.reduce((a, b) => a + b, 0);
      const peak = Math.max(0, ...values);
      const peakIndex = values.indexOf(peak);
      const sorted = [...values].sort((a, b) => b - a);
      let half = 0;
      let window = 0;
      for (const v of sorted) {
        if (half >= total / 2 || total === 0) break;
        half += v;
        window++;
      }
      const third = Math.ceil(n / 3);
      const phaseOfIndex = (i: number): ThroughputPhase => (i < third ? 'warmup' : i < third * 2 ? 'peak' : 'tail');
      const blocks: ThroughputBlock[] = values.map((count, i) => {
        const ms = first + i * blockMs;
        return { at: new Date(ms).toISOString(), ms, count, phase: phaseOfIndex(i), live: ms === liveBlock };
      });
      const sumPhase = (p: ThroughputPhase) => blocks.filter((b) => b.phase === p).reduce((a, b) => a + b.count, 0);
      return {
        day,
        blocks,
        total,
        peak,
        peakIndex,
        peakWindowBlocks: window,
        utilization: n && peak ? env(Math.round(total / n), peak, 'peakBlock') : env(null, null, 'captures'),
        warmup: sumPhase('warmup'),
        peakSum: sumPhase('peak'),
        tail: sumPhase('tail'),
        third,
      };
    });
}

/**
 * Una selección entera como si fuera un solo día: cada bloque de cada evento
 * se proyecta sobre su hora local (11:15 de cualquier fecha cae en el mismo
 * bloque) y se suman las capturas. Responde a «cuándo captura esta cartera»
 * sin una pestaña por evento y día.
 */
export function throughputByClock(list: readonly EventFacts[]): ThroughputSeries | null {
  const base = new Date(2000, 0, 1, 0, 0, 0, 0).getTime();
  const sums = new Map<number, number>();
  for (const f of list) {
    for (const b of f.throughput) {
      const d = new Date(b.at);
      if (!Number.isFinite(d.getTime())) continue;
      const ms = base + d.getHours() * 3_600_000 + Math.floor(d.getMinutes() / BLOCK_MINUTES) * BLOCK_MINUTES * 60_000;
      sums.set(ms, (sums.get(ms) ?? 0) + b.count);
    }
  }
  if (sums.size === 0) return null;
  const raw = [...sums.entries()].map(([ms, count]) => ({ at: new Date(ms).toISOString(), count }));
  const series = throughputOf(raw, 0)[0];
  return series ? { ...series, blocks: series.blocks.map((b) => ({ ...b, live: false })) } : null;
}

// ── Ritmo contra objetivo ──────────────────────────────────────────────────

export type PaceChip = 'noGoal' | 'met' | 'ahead' | 'behind' | 'ofGoal' | 'redeemed' | 'noDenominator';

export interface PaceRead {
  actual: number;
  goal: number | null;
  /** 0–100, lo hecho como parte del objetivo. */
  pct: number;
  /** 0–100, dónde debería estar la barra por el tiempo transcurrido; null sin ritmo. */
  expectedPct: number | null;
  /** Diferencia con lo esperado, en % relativo; null sin ritmo. */
  delta: number | null;
  /** Proyección al cierre a este ritmo; null sin ritmo. */
  projected: number | null;
  chip: PaceChip;
  good: boolean;
}

/**
 * `mode: 'pace'` mide contra el tiempo transcurrido de la ventana (`frac`):
 * «detrás» quiere decir detrás del calendario. `mode: 'ratio'` mide contra un
 * denominador que no corre con el reloj (premios entregados de los ganados),
 * así que no tiene alarma de calendario.
 */
export function paceOf(
  actual: number,
  goal: number | null | undefined,
  opts: { frac?: number | null; live?: boolean; mode?: 'pace' | 'ratio' } = {},
): PaceRead {
  const a = actual || 0;
  const mode = opts.mode ?? 'pace';
  if (mode === 'ratio') {
    const pct = goal ? Math.min(100, (a / goal) * 100) : 0;
    return {
      actual: a,
      goal: goal ?? null,
      pct,
      expectedPct: null,
      delta: null,
      projected: null,
      chip: goal ? 'redeemed' : 'noDenominator',
      good: true,
    };
  }
  if (!goal) {
    return { actual: a, goal: null, pct: 0, expectedPct: null, delta: null, projected: null, chip: 'noGoal', good: true };
  }
  const pct = Math.min(100, (a / goal) * 100);
  if (a >= goal) {
    return { actual: a, goal, pct: 100, expectedPct: null, delta: null, projected: a, chip: 'met', good: true };
  }
  const frac = opts.frac;
  if (!opts.live || frac == null || frac <= 0) {
    return { actual: a, goal, pct, expectedPct: null, delta: null, projected: null, chip: 'ofGoal', good: false };
  }
  const expected = goal * frac;
  const delta = expected ? Math.round(((a - expected) / expected) * 100) : 0;
  return {
    actual: a,
    goal,
    pct,
    expectedPct: Math.min(100, frac * 100),
    delta,
    projected: Math.round(a / frac),
    chip: delta >= 0 ? 'ahead' : 'behind',
    good: delta >= 0,
  };
}

/** El objetivo de una métrica en un evento, o null. */
export function goalOf(f: EventFacts, metric: string): number | null {
  const g = f.goals.find((x) => x.metric === metric);
  return g ? g.target : null;
}

// ── Agregado de una selección ──────────────────────────────────────────────

export interface FactsAggregate {
  events: number;
  live: number;
  upcoming: number;
  done: number;
  funnel: EventFactsFunnel;
  ledger: LedgerTotals;
  leadRate: Metric;
  responseRate: Metric;
  marketingRate: Metric;
  /** Σ de los objetivos de leads de los eventos que lo tienen. */
  leadGoal: number | null;
  first: EventFacts | null;
  last: EventFacts | null;
}

export function aggregateFacts(list: readonly EventFacts[], now = Date.now()): FactsAggregate {
  const funnel = sumFunnels(list);
  const ledger = ledgerOf(mergeLedgers(list));
  let live = 0;
  let upcoming = 0;
  let done = 0;
  let leadGoal: number | null = null;
  for (const f of list) {
    const p = phaseOf(f, now);
    if (p === 'live') live++;
    else if (p === 'upcoming') upcoming++;
    else if (p === 'done') done++;
    const g = goalOf(f, 'leads');
    if (g != null) leadGoal = (leadGoal ?? 0) + g;
  }
  const sorted = [...list].sort((a, b) => (a.event.startDate ?? '').localeCompare(b.event.startDate ?? ''));
  return {
    events: list.length,
    live,
    upcoming,
    done,
    funnel,
    ledger,
    leadRate: env(funnel.leads, funnel.contacts, 'contacts'),
    responseRate: env(funnel.responses, funnel.interactions, 'interactions'),
    marketingRate: env(funnel.marketingOptIns, funnel.leads, 'leads'),
    leadGoal,
    first: sorted[0] ?? null,
    last: sorted[sorted.length - 1] ?? null,
  };
}

/** Tasa de leads de un evento, con su sobre. */
export function leadRateOf(f: EventFacts): Metric {
  return env(f.funnel.leads, f.funnel.contacts, 'contacts');
}
